import React from 'react';
import { DesignThinkingStage, DesignThinkingToolStage } from '../../types';

interface DoubleDiamondNavigationProps {
  activeStage: DesignThinkingStage;
  onStageChange: (stage: DesignThinkingStage) => void;
  designThinkingTools?: DesignThinkingToolStage[];
}

const DoubleDiamondNavigation: React.FC<DoubleDiamondNavigationProps> = ({
  activeStage,
  onStageChange,
  designThinkingTools = []
}) => {
  // Default stage data with fallback labels
  const defaultStages = [
    { stage: 'discover' as DesignThinkingStage, label: 'Discover', fill: '#4A90E2' },
    { stage: 'define' as DesignThinkingStage, label: 'Define', fill: '#2072C7' },
    { stage: 'develop' as DesignThinkingStage, label: 'Develop', fill: '#0F4E93' },
    { stage: 'deliver' as DesignThinkingStage, label: 'Deliver', fill: '#00254D' }
  ];

  // Map API data to stage structure
  const getStagesFromAPI = () => {
    const stageIdMap: Record<number, DesignThinkingStage> = {
      1: 'discover',
      2: 'define',
      3: 'develop',
      4: 'deliver'
    };

    return defaultStages.map(defaultStage => {
      // Find the API stage ID that corresponds to this stage
      const apiStageId = Object.entries(stageIdMap).find(([_, stage]) => stage === defaultStage.stage)?.[0];
      const apiStage = apiStageId ? designThinkingTools.find(s => s.id === Number(apiStageId)) : undefined;
      return {
        ...defaultStage,
        label: apiStage?.name || defaultStage.label
      };
    });
  };

  const stages = designThinkingTools.length > 0 ? getStagesFromAPI() : defaultStages;

  const getPolygonPoints = (stage: DesignThinkingStage) => {
    switch (stage) {
      case 'discover':
      case 'develop':
        return '120,0 0,60 120,120';
      case 'define':
      case 'deliver':
        return '0,0 120,60 0,120';
      default:
        return '120,0 0,60 120,120';
    }
  };

  const getTextX = (stage: DesignThinkingStage) => {
    // For left-pointing triangles (discover, develop), move text to the right
    // For right-pointing triangles (define, deliver), keep centered
    switch (stage) {
      case 'discover':
      case 'develop':
        return '70'; // Move right to center within left-pointing triangle
      case 'define':
      case 'deliver':
        return '60'; // Center for right-pointing triangle
      default:
        return '60';
    }
  };

  return (
    <div className="dd-double-diamond-tabs d-flex justify-content-center align-items-center mb-4" style={{ marginBottom: '2.5rem' }}>
      {stages.map(({ stage, label, fill }) => (
        <div
          key={stage}
          className={`dd-double-diamond-tab diamond-${stage} text-center ${activeStage === stage ? 'active' : ''}`}
          data-stage={stage}
          tabIndex={0}
          aria-label={label}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => onStageChange(stage)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onStageChange(stage);
            }
          }}
        >
          <svg width="200" height="200" viewBox="0 0 120 120">
            <polygon points={getPolygonPoints(stage)} fill={fill} />
            <text
              x={getTextX(stage)}
              y="60"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="20"
              fontFamily="Inter, Arial, sans-serif"
              fontWeight="bold"
              fill="#FFF"
            >
              {label}
            </text>
          </svg>
        </div>
      ))}
    </div>
  );
};

export default DoubleDiamondNavigation;

